import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import api from "../api";
import {
  fetchAtozasConfig,
  restoreAtozasSession,
  endAtozasSession,
  clearAtozasQueryParams,
} from "../services/atozasSso";

const AuthContext = createContext();

// Marks that the current local session came from ATOZAS SSO, so logout knows to
// also tear down the server-side SSO session.
const ATOZAS_SESSION_FLAG = "atozasSession";

// True right after the ATOZAS callback redirect, when we must wait for
// /auth/atozas/me before deciding whether the user is logged in.
const isReturningFromAtozas = () => {
  try {
    return new URLSearchParams(window.location.search).has("atozas");
  } catch (_error) {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem("refreshToken")
  );

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);

  // ATOZAS SSO feature flags, resolved from the backend at startup.
  const [atozas, setAtozas] = useState({
    enabled: false,
    autoRedirect: false,
    checked: false,
  });

  // Only blocks the first render when we are mid-SSO-callback, so the normal
  // Email OTP / Google / anonymous experience is never delayed.
  const [ssoRestoring, setSsoRestoring] = useState(
    () => !localStorage.getItem("authToken") && isReturningFromAtozas()
  );

  // ✅ logout
  const logout = useCallback(() => {
    const hadAtozasSession =
      localStorage.getItem(ATOZAS_SESSION_FLAG) === "1";

    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem(ATOZAS_SESSION_FLAG);

    setToken(null);
    setRefreshToken(null);
    setProfile(null);
    setError(null);
    setProfileLoading(false);

    // Fire-and-forget so existing synchronous callers keep working unchanged.
    // Only runs for sessions that actually came from ATOZAS.
    if (hadAtozasSession) {
      endAtozasSession();
    }
  }, []);

  // ✅ refresh token
  const refreshAccessToken = useCallback(
    async (rToken) => {
      if (!rToken) throw new Error("No refresh token");

      const response = await axios.post(api.refreshToken, { token: rToken });

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem("authToken", accessToken);
      setToken(accessToken);

      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
        setRefreshToken(newRefreshToken);
      }

      return accessToken;
    },
    []
  );

  // ✅ fetch profile
  const fetchUserProfile = useCallback(
    async (authTokenParam) => {
      if (!authTokenParam) return;

      setProfileLoading(true);
      setError(null);

      try {
        const response = await axios.get(api.getProfile, {
          headers: { Authorization: `Bearer ${authTokenParam}` },
        });

        setProfile(response.data);
      } catch (err) {
        // try refresh if 401
        if (err.response?.status === 401 && refreshToken) {
          try {
            const newToken = await refreshAccessToken(refreshToken);

            const retryResponse = await axios.get(api.getProfile, {
              headers: { Authorization: `Bearer ${newToken}` },
            });

            setProfile(retryResponse.data);
          } catch (refreshErr) {
            setError("Session expired. Please login again.");
            logout();
          }
        } else {
          setError(err.response?.data?.message || "Failed to fetch profile");
        }
      } finally {
        setProfileLoading(false);
      }
    },
    [refreshToken, refreshAccessToken, logout]
  );

  // ✅ ONLY RUN when token changes (not on every render)
  useEffect(() => {
    if (token) {
      fetchUserProfile(token);
    } else {
      setProfile(null);
    }
  }, [token, fetchUserProfile]);

  // ✅ login
  const login = useCallback(async (newToken, newRefreshToken = null) => {
    localStorage.setItem("authToken", newToken);
    setToken(newToken);

    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
  }, []);

  // ✅ ATOZAS SSO bootstrap (runs once on application startup)
  // Resolves the feature flag and, when no local JWT exists, asks
  // /auth/atozas/me to restore the application session from the SSO cookie.
  useEffect(() => {
    let cancelled = false;

    const bootstrapAtozas = async () => {
      const config = await fetchAtozasConfig();
      if (cancelled) return;

      setAtozas({ ...config, checked: true });

      // Disabled, or the user already has a local session: nothing to restore.
      if (!config.enabled || localStorage.getItem("authToken")) {
        setSsoRestoring(false);
        return;
      }

      const session = await restoreAtozasSession();
      if (cancelled) return;

      if (session) {
        localStorage.setItem(ATOZAS_SESSION_FLAG, "1");
        await login(session.token, session.refreshToken);
        // Drop the ?atozas=1 marker so a refresh is a plain page load.
        clearAtozasQueryParams();
      }

      setSsoRestoring(false);
    };

    bootstrapAtozas();

    return () => {
      cancelled = true;
    };
  }, [login]);

  const isLoggedIn = useMemo(() => !!token, [token]);

  const getRoles = useCallback(() => {
    return profile?.role || [];
  }, [profile]);

  const getBlockedRoles = useCallback(() => {
    return profile?.blocked || [];
  }, [profile]);

  const getUnblockedRoles = useCallback(() => {
    const roles = profile?.role || [];
    const blocked = profile?.blocked || [];
    return roles.filter((r) => !blocked.includes(r));
  }, [profile]);

  const setUserStorage = useCallback(
    (key, value) => {
      if (!token) return;
      localStorage.setItem(`${token}_${key}`, JSON.stringify(value));
    },
    [token]
  );

  const getUserStorage = useCallback(
    (key) => {
      if (!token) return null;
      const stored = localStorage.getItem(`${token}_${key}`);
      return stored ? JSON.parse(stored) : null;
    },
    [token]
  );

  return (
    <AuthContext.Provider
      value={{
        token,
        refreshToken,
        profile,
        profileLoading,
        error,
        login,
        logout,
        refreshAccessToken,
        fetchUserProfile,
        isLoggedIn,
        getRoles,
        getBlockedRoles,
        getUnblockedRoles,
        setUserStorage,
        getUserStorage,
        atozasEnabled: atozas.enabled,
        atozasAutoRedirect: atozas.autoRedirect,
        atozasChecked: atozas.checked,
        ssoRestoring,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
